import type { ComponentType, ReactNode } from "react";
import {
  LayoutDashboardIcon,
  UsersIcon,
  PackageIcon,
  UserCogIcon,
  PaletteIcon,
  FileTextIcon,
  Building2Icon,
  HardDriveIcon,
  UserIcon,
  ActivityIcon,
  CpuIcon,
  BugIcon,
} from "lucide-react";
import { createElement } from "react";
import { STRINGS } from "@/constants/strings";

export type ShellType = "tenant" | "admin" | "patient";

export type Role =
  | "superadmin"
  | "admin_hcp"
  | "tecnico"
  | "medico_hcp"
  | "infermiere_hcp"
  | "admin_clinico"
  | "medico"
  | "infermiere"
  | "patient";

export interface SidebarItem {
  title: string;
  path: string;
  icon: ReactNode;
  allowedRoles: Role[];
}

export interface RouteDefinition {
  path: string;
  allowedRoles: Role[];
  page: () => Promise<{ default: ComponentType }>;
}

export const TENANT_SIDEBAR: SidebarItem[] = [
  {
    title: STRINGS.nav.dashboard,
    path: "/",
    icon: createElement(LayoutDashboardIcon),
    allowedRoles: [
      "admin_hcp",
      "medico_hcp",
      "infermiere_hcp",
      "admin_clinico",
      "medico",
      "infermiere",
    ],
  },
  {
    title: STRINGS.nav.patients,
    path: "/patients",
    icon: createElement(UsersIcon),
    allowedRoles: [
      "admin_hcp",
      "tecnico",
      "medico_hcp",
      "infermiere_hcp",
      "medico",
      "infermiere",
    ],
  },
  {
    title: STRINGS.nav.bundles,
    path: "/bundles",
    icon: createElement(PackageIcon),
    allowedRoles: ["admin_hcp", "tecnico"],
  },
  {
    title: STRINGS.nav.users,
    path: "/users",
    icon: createElement(UserCogIcon),
    allowedRoles: ["admin_hcp", "admin_clinico"],
  },
  {
    title: STRINGS.nav.branding,
    path: "/branding",
    icon: createElement(PaletteIcon),
    allowedRoles: ["admin_hcp", "admin_clinico"],
  },
  {
    title: STRINGS.nav.auditLog,
    path: "/audit",
    icon: createElement(FileTextIcon),
    allowedRoles: ["admin_hcp", "admin_clinico"],
  },
];

export const ADMIN_SIDEBAR: SidebarItem[] = [
  {
    title: STRINGS.nav.dashboard,
    path: "/",
    icon: createElement(LayoutDashboardIcon),
    allowedRoles: ["superadmin"],
  },
  {
    title: STRINGS.nav.tenants,
    path: "/tenants",
    icon: createElement(Building2Icon),
    allowedRoles: ["superadmin"],
  },
  {
    title: STRINGS.nav.bundles,
    path: "/bundles",
    icon: createElement(PackageIcon),
    allowedRoles: ["superadmin"],
  },
  {
    title: STRINGS.nav.devices,
    path: "/devices",
    icon: createElement(CpuIcon),
    allowedRoles: ["superadmin"],
  },
  {
    title: STRINGS.nav.auditLog,
    path: "/audit",
    icon: createElement(FileTextIcon),
    allowedRoles: ["superadmin"],
  },
  {
    title: STRINGS.nav.s3RawData,
    path: "/s3-raw",
    icon: createElement(HardDriveIcon),
    allowedRoles: ["superadmin"],
  },
  {
    title: STRINGS.nav.streamVisualizer,
    path: "/debug/stream-visualizer",
    icon: createElement(BugIcon),
    allowedRoles: ["superadmin"],
  },
];

export const PATIENT_SIDEBAR: SidebarItem[] = [
  {
    title: STRINGS.nav.myProfile,
    path: "/",
    icon: createElement(UserIcon),
    allowedRoles: ["patient"],
  },
  {
    title: STRINGS.nav.mySessions,
    path: "/sessions",
    icon: createElement(ActivityIcon),
    allowedRoles: ["patient"],
  },
];

export function getSidebarItems(shell: ShellType): SidebarItem[] {
  switch (shell) {
    case "tenant":
      return TENANT_SIDEBAR;
    case "admin":
      return ADMIN_SIDEBAR;
    case "patient":
      return PATIENT_SIDEBAR;
  }
}

export function resolveShell(hostname: string): {
  shell: ShellType;
  slug: string | null;
} {
  if (
    import.meta.env.DEV &&
    (hostname === "localhost" || hostname === "127.0.0.1")
  ) {
    const devShell = import.meta.env.VITE_DEV_SHELL as ShellType | undefined;
    if (devShell === "admin") return { shell: "admin", slug: null };
    if (devShell === "patient") return { shell: "patient", slug: null };
    const devSlug = import.meta.env.VITE_DEV_SLUG as string | undefined;
    return { shell: "tenant", slug: devSlug ?? null };
  }

  const parts = hostname.split(".");
  const subdomain = parts[0];

  if (subdomain === "admin") {
    return { shell: "admin", slug: null };
  }
  if (subdomain === "my") {
    return { shell: "patient", slug: null };
  }

  return { shell: "tenant", slug: subdomain ?? null };
}
